import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { SupplierOrderDto } from '../../../dto/supplierOrder';
import { fetchSupplierOrder } from '../api/supplierOrdersApi';

interface UseSupplierOrderDetailResult {
  supplierOrder: SupplierOrderDto | null;
  setSupplierOrder: Dispatch<SetStateAction<SupplierOrderDto | null>>;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<SupplierOrderDto>;
}

export function useSupplierOrderDetail(supplierOrderId?: string): UseSupplierOrderDetailResult {
  const [supplierOrder, setSupplierOrder] = useState<SupplierOrderDto | null>(null);
  const [loading, setLoading] = useState(Boolean(supplierOrderId));
  const [error, setError] = useState<unknown>(null);

  const refresh = async () => {
    if (!supplierOrderId) {
      throw new Error('Supplier order id is required');
    }

    setLoading(true);
    setError(null);

    try {
      const next = await fetchSupplierOrder(supplierOrderId);
      setSupplierOrder(next);
      return next;
    } catch (nextError) {
      setError(nextError);
      throw nextError;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    if (!supplierOrderId) {
      setSupplierOrder(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const next = await fetchSupplierOrder(supplierOrderId);
        if (isActive) {
          setSupplierOrder(next);
        }
      } catch (nextError) {
        if (isActive) {
          setError(nextError);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [supplierOrderId]);

  return {
    supplierOrder,
    setSupplierOrder,
    loading,
    error,
    refresh,
  };
}
